import React, {useEffect, useRef} from "react";
import {Box, Button, Icon, Stack, View, VisuallyHidden} from "@unibuddy/patron";
import styled from "styled-components";

import {
    AmbassadorProfileInformation,
    useGetRandomAmbassadorsQuery,
} from "../../../../types/generated";
import {useUniversityContext} from "../../../../providers/UniversityProvider";
import {IconClose} from "../../../../icons";
import {ChatHeaderContentProps} from "./ChatHeaderContent";
import {useScreensizeContext} from "../../../../providers/ScreensizeProvider";

const AMBASSADOR_AVATAR_COUNT = 3;

const StyledHeaderContent = styled.span`
    display: block;

    &:before {
        content: "";
        margin-bottom: -0.374em;
        display: table;
    }
    &:after {
        content: "";
        margin-top: -0.421em;
        display: table;
    }
`;

const StyledHeadingUpper = styled(StyledHeaderContent)<{$isSmallDeviceDimension: boolean}>`
    font-size: ${({$isSmallDeviceDimension}) => ($isSmallDeviceDimension ? "0.875rem" : "1rem")};
    line-height: ${({$isSmallDeviceDimension}) => ($isSmallDeviceDimension ? "21px" : "24px")};
    height: 11.289px;
    font-weight: 400;
    margin-bottom: ${({$isSmallDeviceDimension}) =>
        $isSmallDeviceDimension ? "0.875rem" : "1rem"};
`;

const StyledHeadingLower = styled(StyledHeaderContent)<{$isSmallDeviceDimension: boolean}>`
    font-size: ${({$isSmallDeviceDimension}) => ($isSmallDeviceDimension ? "1.5rem" : "2rem")};
    line-height: ${({$isSmallDeviceDimension}) => ($isSmallDeviceDimension ? "36px" : "48px")};
    height: 22.57px;
    font-weight: 700;
`;

const Avatar = styled(Box)`
    overflow: hidden;
    width: 64px;
    height: 64px;
    border: 5px solid white;
    border-radius: 64px;
    object-fit: cover;
    &:not(:first-child) {
        margin-left: -20px;
    }
`;

function DynamicHeaderContent() {
    const {universityId} = useUniversityContext();

    const {data, loading, error} = useGetRandomAmbassadorsQuery({
        variables: {universityId, count: AMBASSADOR_AVATAR_COUNT},
    });

    if (loading) {
        return (
            <View flexDirection="row">
                <Avatar bgColor="grey100" />
                <Avatar bgColor="grey100" />
                <Avatar bgColor="grey100" />
                <VisuallyHidden>Loading avatars</VisuallyHidden>
            </View>
        );
    }

    if (error || !data?.getRandomAmbassadors) {
        return null;
    }

    return (
        <Stack space="small-medium" align="center">
            {data?.getRandomAmbassadors?.length === AMBASSADOR_AVATAR_COUNT && (
                <View flexDirection="row">
                    {data.getRandomAmbassadors.map(
                        (ambassador: Partial<AmbassadorProfileInformation>) => (
                            <Avatar
                                as="img"
                                src={ambassador.profilePhoto}
                                alt={
                                    ambassador.firstName
                                        ? `${ambassador.firstName} profile photo`
                                        : "Profile photo"
                                }
                                key={ambassador.profilePhoto}
                            />
                        ),
                    )}
                </View>
            )}
            {/* Uncomment for https://unibuddy.atlassian.net/browse/IBEX-2191 */}
            {/* <Text>
                ⚡︎ <TextLine weight="bold">34 students</TextLine> helped today
            </Text> */}
        </Stack>
    );
}

export function ChatHeaderIntroContent({handleCloseClicked}: Readonly<ChatHeaderContentProps>) {
    const {isSmallDeviceDimension, isSmallInnerHeight} = useScreensizeContext();
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);

    const headerPadding = isSmallDeviceDimension
        ? isSmallInnerHeight
            ? "small"
            : "medium-large"
        : "large";

    useEffect(() => {
        const messageListener = (e: MessageEvent) => {
            if (e.data === "chatVisible=true") {
                if (isSmallDeviceDimension) {
                    closeButtonRef.current?.focus();
                }
            }
        };

        window.addEventListener("message", messageListener, true);

        return () => {
            window.removeEventListener("message", messageListener);
        };
    }, [isSmallDeviceDimension]);

    return (
        <Box position="relative">
            <Box
                position="absolute"
                display={isSmallDeviceDimension ? "block" : "none"}
                top={12}
                right={12}
            >
                <Button
                    iconOnly
                    round
                    clear
                    size="sm"
                    color="light"
                    onClick={handleCloseClicked}
                    aria-label="Close assistant"
                    ref={closeButtonRef}
                >
                    <Icon icon={IconClose} size="medium" />
                </Button>
            </Box>
            {/* id must match chatContainer.setAttribute("aria-labelledby... in the embed.js script */}
            <View
                id="assistant-heading"
                h="fit-content"
                minH="fit-content"
                w="100%"
                minW="100%"
                padding={headerPadding}
                alignItems="center"
                gap="small-medium"
            >
                {/* We cannot use Patron Heading and Text to achieve a11y result that matches the design  */}
                {/* See: https://unibuddy.atlassian.net/browse/IBEX-2718 */}
                <h2 style={{textAlign: "center", margin: 0}}>
                    <StyledHeadingUpper $isSmallDeviceDimension={isSmallDeviceDimension}>
                        Welcome to
                    </StyledHeadingUpper>
                    <StyledHeadingLower $isSmallDeviceDimension={isSmallDeviceDimension}>
                        Your Assistant
                    </StyledHeadingLower>
                </h2>
                {!isSmallInnerHeight && <DynamicHeaderContent />}
            </View>
        </Box>
    );
}
